// FR-M9: what the Meetings page (or Settings' Calendars tab) says after the calendar OAuth round
// trip. The server's `/auth/calendar/{start,callback}` routes redirect back to `/o/{slug}/meetings`
// (or `/account?tab=calendars`) with either
// `?connected={provider}` or `?calendar_error={code}&provider={provider}`. `code` is a fixed
// vocabulary (server/src/calendar.rs `calendar_error_code`) — provider error text is never
// reflected, and an unknown code still gets a generic message rather than being echoed.

export type CalendarProvider = "google" | "microsoft";

export function calendarProviderLabel(provider: string | null): string {
  if (provider === "google") return "Google Calendar";
  if (provider === "microsoft") return "Microsoft 365";
  return "Your calendar";
}

export interface ConnectNotice {
  tone: "success" | "danger";
  message: string;
}

export function calendarConnectNotice(params: URLSearchParams): ConnectNotice | null {
  const connected = params.get("connected");
  if (connected === "google" || connected === "microsoft") {
    return {
      tone: "success",
      message: `Connected ${calendarProviderLabel(connected)}. Upcoming meetings now sync from it.`,
    };
  }
  const code = params.get("calendar_error");
  if (!code) return null;
  const label = calendarProviderLabel(params.get("provider"));
  switch (code) {
    case "access_denied":
      return {
        tone: "danger",
        message: `${label} wasn't connected — access was not granted on the consent screen.`,
      };
    case "provider_unconfigured":
      return {
        tone: "danger",
        message: `${label} can't be connected on this Grackle server yet: its OAuth app isn't configured.`,
      };
    case "provider_unreachable":
      return { tone: "danger", message: `Couldn't reach ${label} to start connecting. Try again in a minute.` };
    case "token_exchange":
      return { tone: "danger", message: `${label} didn't finish the connect handshake. Try connecting again.` };
    case "invalid_state":
      return { tone: "danger", message: "That calendar connect link expired or was already used. Try connecting again." };
    case "org_gone":
      return { tone: "danger", message: "The workspace this calendar was being connected to no longer exists." };
    default:
      return { tone: "danger", message: `${label} reported an error while connecting. Try again.` };
  }
}
